import { defineStore } from 'pinia';
import { toast } from "@/utils/index.js";
import { AppCheckUpdate } from "@/api/app.js"
import { GetSystemBasic } from "@/api/base.js"
import { appSystemConfig } from "@/app.config.js"
import i18n from "@/locale/index.js"

const { t } = i18n.global;
const SystemBasicConfigKey = 'systemBasicConfig';
let systemBasicRequest = null;

const normalizeSystemBasicConfig = (config = {}) => {
	return {
		logo: config.logo || appSystemConfig.logo,
		name: config.name || appSystemConfig.name
	}
}

export const useSystemStore = defineStore('system', () => {
	const systemInfo = uni.getSystemInfoSync();
	const statusBarHeight = systemInfo.statusBarHeight;
	// #ifdef MP
	const { top, height, left } = uni.getMenuButtonBoundingClientRect();
	const navbarHeight = height + (top - statusBarHeight) * 2;
	const miniProgramMenuSafeArea = systemInfo.windowWidth - left;
	// #endif
	// #ifndef MP
	const navbarHeight = 44;
	const miniProgramMenuSafeArea = 0;
	// #endif
	return { 
		systemInfo,
		statusBarHeight,
		navbarHeight,
		miniProgramMenuSafeArea
	};
});

export const useAppSystemConfigStore = defineStore('appSystemConfig', {
	state: () => ({
		basic: normalizeSystemBasicConfig(uni.getStorageSync(SystemBasicConfigKey) || {}),
		isLoaded: false
	}),
	getters: {
		logo: (state) => state.basic.logo || appSystemConfig.logo,
		name: (state) => state.basic.name || appSystemConfig.name
	},
	actions: {
		onSetSystemBasic(config = {}) {
			this.basic = normalizeSystemBasicConfig(config);
			uni.setStorageSync(SystemBasicConfigKey, this.basic);
			this.syncH5Document();
		},
		onGetSystemBasic(force = false) {
			this.syncH5Document();
			if (this.isLoaded && !force) return Promise.resolve(this.basic);
			if (systemBasicRequest && !force) return systemBasicRequest;

			systemBasicRequest = GetSystemBasic().then((res) => {
				const config = res.result || res.data || {};
				this.onSetSystemBasic(config);
				this.isLoaded = true;
				return this.basic;
			}).finally(() => {
				systemBasicRequest = null;
			});

			return systemBasicRequest;
		},
		syncH5Document() {
			// #ifdef H5
			if (typeof document === 'undefined') return;

			document.title = this.name;

			const logo = this.logo;
			// 移除旧节点再插入新节点，确保 iOS Safari 在"添加到主屏幕"时读取到最新图标
			['icon', 'apple-touch-icon'].forEach(rel => {
				document.querySelectorAll(`link[rel="${rel}"]`).forEach(el => el.remove());
				if (!logo) return;
				const link = document.createElement('link');
				link.rel = rel;
				link.href = logo;
				if (rel === 'apple-touch-icon') link.setAttribute('sizes', '180x180');
				document.head.appendChild(link);
			});

			const appTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
			appTitle && appTitle.setAttribute('content', this.name);
			// #endif
		}
	}
});

export const useMiniUpdate = defineStore('miniUpdate', {
	actions:{
		onUpdate(){
			const updateManager = uni.getUpdateManager();
			
			updateManager.onCheckForUpdate(function (res) {
			  // 请求完新版本信息的回调
			  console.log(res.hasUpdate);
			});
			
			updateManager.onUpdateReady(function (res) {
			  uni.showModal({
			    title: '更新提示',
			    content: '新版本已经准备好，是否重启应用？',
			    success(res) {
			      if (res.confirm) {
			        // 新的版本已经下载好，调用 applyUpdate 应用新版本并重启
			        updateManager.applyUpdate();
			      }
			    }
			  });
			});
			
			updateManager.onUpdateFailed(function (res) {
			  // 新的版本下载失败
			});
		}
	}
})

export const useAppUpdateStore = defineStore('appUpdate', {
	state: () => ({
		updateInfo: {
			version: '',
			info: ''
		}, //更新信息
		isUpdate: false, //是否有更新
		isForce: false, //是否强制更新
		isHot: false,  //是否热更新
		isPackage: true, //是否整包更新
		isDownload: false, //是否下载
		downloadProgress: 0, //下载进度
		isInstall: false, // 是否安装
		isCancel: false, // 是否忽略安装
		version: '1.0.0', // 当前App版本号
	}),
	actions: {
		// 检查更新
		onCheckForUpdate(){	
			return new Promise((resolve, reject) => {
				plus.runtime.getProperty(plus.runtime.appid, async widgetInfo => {
					try{
						this.version = widgetInfo.version;
						
						const { result } = await AppCheckUpdate();
						
						this.updateInfo = result;
						
						// 没有更新
						if(this.updateInfo === null){
							resolve();
							return;
						};
						
						//当前版本
						const appVersions = Number(widgetInfo.version.replace(/\./g,''));
						
						//服务器版本
						const serverVersions = Number(this.updateInfo.version.replace(/\./g,''));
						
						// this.isCancel = (uni.getStorageSync('updateVersion') === this.updateInfo.version);
						// console.log(this.updateInfo)
						
						// 是否更新
						this.isUpdate = serverVersions > appVersions;
						
						resolve();
					}catch(err){
						//TODO handle the exception
						reject(err);
					}
				});
			})
		},
		// 开始更新
		onUpdate(){
			if(plus.os.name === "iOS" && this.isPackage){
				plus.runtime.openURL(this.updateInfo.apkUrl)
				return;
			}
			
			try{
				this.isDownload = true;
				const downloadTask =  uni.downloadFile({
					url: this.isPackage ? this.updateInfo.apkUrl : this.updateInfo.wgtUrl,  
					success: (downloadResult) => {
						if (downloadResult.statusCode === 200) {
							this.isInstall = true;
							plus.runtime.install(downloadResult.tempFilePath, { force: true }, function() {
								plus.runtime.restart();
							}, function(error) {
								plus.nativeUI.alert(error.message, function(){
									plus.runtime.restart();
								}, t('appUpdate.faill'), t('confirm'));
							});  
						}
				   },
				   fail(error){
						plus.nativeUI.alert(t('appUpdate.downloadFaill'), function(){
							plus.runtime.restart(); 
						}, '', t('confirm'));
				   },
				   complete(e){
					   console.log(e);
					   if(e.statusCode !== 200){
						   plus.nativeUI.alert(t('appUpdate.downloadFaill'), function(){
						   	plus.runtime.restart(); 
						   }, '', t('confirm'));
					   }
				   }
				});  
				
				downloadTask.onProgressUpdate((res) => {
					this.downloadProgress = res.progress;
				});
			}catch(e){
				//TODO handle the exception
				console.log(e);
			}
		}
	}
});
